import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHybridFocusable } from '../../../../hooks/useHybridFocusable'

import {
  getTTSDuration,
  platformTransitionStyle,
  toHHMMSS,
} from '../../../../../../shared/helpers.js'

import { useNavigationMode } from '../../../../../../shared/providers/NavigationModeProvider/NavigationModeProvider'
import { useAppSelector } from '../../../../hooks/useStore'
import { useVideoPlayerData } from '../../VideoPlayerContext'
import { calculateSeekTimeFromPointer, isVodContent } from '../pointerUtils'
import { Tooltip } from './Tooltip'
import { TrickPlay } from './TrickPlay'
import { useVTTParser } from './useVTTParser'

import tts from '../../../../lib/TTS.js'
import type { ProgressBarProps } from './types'

import dayjs from 'dayjs'
import {
  KeyTypeEnum,
  useKeyDown,
} from '../../../../../../shared/providers/KeyDownProvider'

export default function ProgressBar(props: ProgressBarProps) {
  const videoDetails = useAppSelector((state) => state.content.videoDetails)
  const {
    playerState,
    setPlayerStatus,
    setIsUserTryingToSeek,
    setLastUserInteraction,
  } = useVideoPlayerData()
  const trickImages = useVTTParser(videoDetails.trickPlayUrl || '')
  const seekFraction = Math.max(10, Math.ceil(playerState.duration / 120))
  const isAdPlaying =
    playerState.status === 'adPlaying' || playerState.status === 'adPaused'
  const { isCursorMode } = useNavigationMode()
  const progressTrackRef = useRef<HTMLDivElement | null>(null)
  const [seekLevel, setSeekLevel] = useState(0) //-3 | -2 | -1 | 0 | 1 | 2 | 3
  const [seekHeadPosition, setSeekHeadPosition] = useState(0)
  const [progressBarWidth, setProgressBarWidth] = useState(
    (100 / playerState.duration) * (playerState.currentProgress + 1)
  )
  const seekIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const ttsIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isHandlingRewindToZeroRef = useRef(false)
  const vodSeekEnabled = isVodContent(videoDetails)
  const pointerSeekEnabled =
    isCursorMode &&
    vodSeekEnabled &&
    !isAdPlaying &&
    typeof props.seek === 'function' &&
    playerState.duration > 0

  useKeyDown(
    [
      KeyTypeEnum.FastForward,
      KeyTypeEnum.Rewind,
      KeyTypeEnum.Pause,
      KeyTypeEnum.Play,
    ],
    (key) => {
      if (key === KeyTypeEnum.FastForward) {
        jumpForward()
      } else if (key === KeyTypeEnum.Rewind) {
        jumpBackward()
      } else if (key === (KeyTypeEnum.Pause || KeyTypeEnum.PlayPause)) {
        if (playerState.status === 'seeking') {
          executeSeek()
        } else {
          props.playOrPause()
        }
      } else if (key === KeyTypeEnum.Play) {
        if (playerState.status === 'seeking') {
          executeSeek()
        } else if (['adPaused', 'paused'].includes(playerState.status)) {
          props.playOrPause()
        }
      }
    }
  )

  const currentPosition = playerState.isUserTryingToSeek
    ? seekHeadPosition
    : playerState.currentProgress
  let timeRemaining: string | number
  if (isAdPlaying) {
    timeRemaining = Math.max(
      0,
      Math.floor(
        (playerState.adData?.adDuration ?? 0) -
          (playerState.adData?.adCurrentTime ?? 0)
      )
    )
  } else {
    timeRemaining = toHHMMSS(playerState.duration - currentPosition)
  }
  const showTrickPlay =
    !isAdPlaying && Object.keys(trickImages || {}).length > 0

  useEffect(() => {
    if (playerState.isUserTryingToSeek) {
      const newWidth = (100 / playerState.duration) * seekHeadPosition
      setProgressBarWidth(newWidth)
    } else {
      const newWidth =
        (100 / playerState.duration) *
        ((isAdPlaying
          ? playerState.adData?.adCurrentTime ?? 0
          : playerState.currentProgress) +
          1)
      setProgressBarWidth(newWidth)
    }
  }, [
    playerState.isUserTryingToSeek,
    playerState.duration,
    seekHeadPosition,
    isAdPlaying,
    playerState.adData?.adCurrentTime,
    playerState.currentProgress,
  ])

  const executeSeek = () => {
    if (playerState.status === 'seeking') {
      setIsUserTryingToSeek(false)
      tts.stopRead()
      props.seek && props.seek(seekHeadPosition)
      setSeekLevel(0)
      setSeekHeadPosition(0)
    }
  }

  const { ref, focusSelf, focused } = useHybridFocusable({
    focusKey: 'progress-bar',
    onEnterPress: () => {
      if (playerState.status === 'seeking') {
        executeSeek()
      } else if (!playerState.upNextData?.isUpNextVisible) {
        props.playOrPause()
      }
    },
    onArrowPress: (direction: string): boolean => {
      let continueNavigation = true
      if (isAdPlaying) {
        continueNavigation = false
      } else if (direction === 'left') {
        jumpBackward()
        continueNavigation = false
      } else if (direction === 'right') {
        jumpForward()
        continueNavigation = false
      }
      return continueNavigation
    },
  })

  useEffect(() => {
    if (!focused && seekLevel !== 0) {
      tts.stopRead()
      setSeekLevel(0)
      setSeekHeadPosition(0)
      props.play()
    }
  }, [focused, seekLevel])

  const jumpBackward = () => {
    props.pause()
    setIsUserTryingToSeek(true)
    setSeekLevel((prev) => {
      const newLevel = Math.max(-3, prev - 1)
      if (newLevel !== 0) {
        updateSeekHeadPosition(newLevel)
      }
      return newLevel
    })
  }

  const jumpForward = () => {
    props.pause()
    setIsUserTryingToSeek(true)
    setSeekLevel((prev) => {
      const newLevel = Math.min(3, prev + 1)
      updateSeekHeadPosition(newLevel)
      return newLevel
    })
  }

  const updateSeekHeadPosition = (newSeekLevel?: number) => {
    setSeekHeadPosition((prev) => {
      const position = prev === 0 ? playerState.currentProgress : prev
      return Math.max(
        0,
        Math.min(
          playerState.duration,
          position + seekFraction * (newSeekLevel || seekLevel)
        )
      )
    })
  }

  const setTTSInterval = (seekLevel: number) => {
    function clearTTSInterval() {
      if (ttsIntervalRef.current) {
        clearInterval(ttsIntervalRef.current)
        ttsIntervalRef.current = null
      }
    }

    clearTTSInterval()
    if (seekLevel === 0 && playerState.status === 'seeking') {
      tts.addTextToRead(`Paused at ${getTTSDuration(seekHeadPosition)}`)
    }
    if (seekLevel !== 0) {
      let text = 'Fast Forwarding'
      if (seekLevel < 0) {
        text = 'Rewinding'
      }
      tts.addTextToRead(`${text} ${Math.abs(seekLevel)}X`)
      ttsIntervalRef.current = setInterval(() => {
        tts.addTextToRead(`${text} ${Math.abs(seekLevel)}X`)
      }, 5000)
    }

    return clearTTSInterval
  }

  useEffect(() => {
    if (playerState.status !== 'seeking' && playerState.isUserTryingToSeek) {
      setPlayerStatus('seeking')
    }
  }, [playerState.status, playerState.isUserTryingToSeek, setPlayerStatus])

  useEffect(() => {
    if (
      seekLevel < 0 &&
      seekHeadPosition === 0 &&
      playerState.status === 'seeking' &&
      !isHandlingRewindToZeroRef.current
    ) {
      isHandlingRewindToZeroRef.current = true

      setSeekLevel(0)
      setIsUserTryingToSeek(false)
      tts.stopRead()
      props.seek?.(0)
      props.play?.()

      setTimeout(() => {
        isHandlingRewindToZeroRef.current = false
      }, 100)
    }
  }, [seekHeadPosition, seekLevel, playerState.status, setIsUserTryingToSeek])

  useEffect(() => {
    const clearTTSIntervalRef = setTTSInterval(seekLevel)
    function clearIntervalRef() {
      if (seekIntervalRef.current) {
        clearInterval(seekIntervalRef.current)
        seekIntervalRef.current = null
      }
    }
    clearIntervalRef()
    if (seekLevel !== 0) {
      setPlayerStatus('seeking')
      seekIntervalRef.current = setInterval(() => {
        updateSeekHeadPosition()
      }, 1000)
    } else {
      clearIntervalRef()
    }
    return () => {
      clearIntervalRef()
      clearTTSIntervalRef()
    }
  }, [seekLevel])

  useEffect(() => {
    focusSelf()

    return () => {
      if (playerState.status === 'seeking') {
        props.play()
        setPlayerStatus('playing')
      }
    }
  }, [focusSelf])

  useEffect(() => {
    if (!playerState.isUserTryingToSeek) {
      setSeekLevel(0)
      setSeekHeadPosition(0)
    }
  }, [playerState.isUserTryingToSeek])

  const handlePointerSeek = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!pointerSeekEnabled || !progressTrackRef.current || !props.seek)
        return

      const rect = progressTrackRef.current.getBoundingClientRect()
      if (!rect.width || playerState.duration <= 0) return

      const seekTime = calculateSeekTimeFromPointer({
        clientX: event.clientX,
        left: rect.left,
        width: rect.width,
        duration: playerState.duration,
      })
      event.preventDefault()
      event.stopPropagation()
      tts.stopRead()
      setPlayerStatus('seeking')
      setIsUserTryingToSeek(false)
      setLastUserInteraction(dayjs())
      props.setIsSeekingViaPointer?.(true)
      props.seek(seekTime)
    },
    [
      pointerSeekEnabled,
      props.seek,
      props.setIsSeekingViaPointer,
      playerState.duration,
      setIsUserTryingToSeek,
      setLastUserInteraction,
      setPlayerStatus,
    ]
  )

  return (
    <div
      id="control-bar"
      style={{
        bottom: '-305px',
        backgroundColor: '#2F3437',
        transition: platformTransitionStyle('bottom 500ms linear'),
        pointerEvents: isCursorMode ? 'auto' : 'none',
      }}
      ref={ref}
    >
      <div
        id="progress-track"
        style={{
          transition: platformTransitionStyle('opacity 250ms linear'),
          right: showTrickPlay ? '170px' : '90px',
          left: showTrickPlay ? '170px' : '90px',
          cursor: pointerSeekEnabled ? 'pointer' : 'default',
        }}
        ref={progressTrackRef}
        onPointerDown={pointerSeekEnabled ? handlePointerSeek : undefined}
      >
        <div
          id="progress"
          style={{
            width: `${progressBarWidth}%`,
            transition: platformTransitionStyle(
              seekHeadPosition !== 0 ? 'width 1s linear' : 'width 0s linear'
            ),
          }}
        >
          {!isAdPlaying && (
            <>
              {!showTrickPlay ? (
                <Tooltip
                  controlsPosition={currentPosition}
                  seekLevel={seekLevel}
                />
              ) : (
                <TrickPlay
                  controlsPosition={currentPosition}
                  trickImages={trickImages || {}}
                  seekLevel={seekLevel}
                  trickPlayUrl={videoDetails.trickPlayUrl || ''}
                  isPaused={
                    playerState.status === 'paused' ||
                    playerState.status === 'adPaused' ||
                    (playerState.status === 'seeking' && seekLevel === 0)
                  }
                />
              )}
            </>
          )}
        </div>
        <div id="time-remaining">-{timeRemaining}</div>
      </div>
    </div>
  )
}
